import React, { useState } from 'react';
import { Wand2, Copy, Check } from 'lucide-react';

type ComponentType = 'layout' | 'form' | 'card' | 'navigation' | 'hero' | 'footer';
type StyleType = 'minimal' | 'modern' | 'classic' | 'custom';

const generateHTML = (type: ComponentType, style: StyleType, customElements: string): string => {
  // This is a simple example. In a real app, you'd use more sophisticated generation logic
  const templates = {
    layout: {
      minimal: `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Simple Layout</title>
</head>
<body>
    <header>
        <nav>
            <ul>
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About</a></li>
                <li><a href="#contact">Contact</a></li>
            </ul>
        </nav>
    </header>
    <main>
        <section id="content">
            <h1>Welcome</h1>
            <p>Your content goes here</p>
        </section>
    </main>
    <footer>
        <p>&copy; 2025 Your Company</p>
    </footer>
</body>
</html>`,
      modern: `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Modern Layout</title>
    <link rel="stylesheet" href="styles.css">
</head>
<body>
    <div class="container">
        <header class="main-header">
            <nav class="nav-container">
                <div class="logo">
                    <a href="#home">Your Logo</a>
                </div>
                <ul class="nav-links">
                    <li><a href="#home">Home</a></li>
                    <li><a href="#about">About</a></li>
                    <li><a href="#services">Services</a></li>
                    <li><a href="#contact">Contact</a></li>
                </ul>
            </nav>
        </header>
        
        <main class="main-content">
            <section class="hero">
                <h1>Welcome to Our Site</h1>
                <p>Discover amazing content and services</p>
            </section>
            
            <section class="features">
                <h2>Our Features</h2>
                <div class="feature-grid">
                    <!-- Feature items go here -->
                </div>
            </section>
        </main>

        <footer class="main-footer">
            <div class="footer-content">
                <div class="footer-info">
                    <h3>Contact Us</h3>
                    <p>Email: info@example.com</p>
                    <p>Phone: (555) 123-4567</p>
                </div>
                <div class="footer-links">
                    <h3>Quick Links</h3>
                    <ul>
                        <li><a href="#privacy">Privacy Policy</a></li>
                        <li><a href="#terms">Terms of Service</a></li>
                    </ul>
                </div>
            </div>
            <div class="footer-bottom">
                <p>&copy; 2025 Your Company. All rights reserved.</p>
            </div>
        </footer>
    </div>
</body>
</html>`
    },
    form: {
      minimal: `
<form action="/submit" method="POST">
    <div class="form-group">
        <label for="name">Name:</label>
        <input type="text" id="name" name="name" required>
    </div>
    <div class="form-group">
        <label for="email">Email:</label>
        <input type="email" id="email" name="email" required>
    </div>
    <div class="form-group">
        <label for="message">Message:</label>
        <textarea id="message" name="message" rows="4" required></textarea>
    </div>
    <button type="submit">Submit</button>
</form>`,
      modern: `
<form class="contact-form" action="/submit" method="POST">
    <div class="form-header">
        <h2>Contact Us</h2>
        <p>We'll get back to you as soon as possible</p>
    </div>
    
    <div class="form-group">
        <label for="name" class="form-label">Full Name</label>
        <input 
            type="text" 
            id="name" 
            name="name" 
            class="form-input"
            placeholder="John Doe"
            required
        >
    </div>
    
    <div class="form-group">
        <label for="email" class="form-label">Email Address</label>
        <input 
            type="email" 
            id="email" 
            name="email" 
            class="form-input"
            placeholder="john@example.com"
            required
        >
    </div>
    
    <div class="form-group">
        <label for="subject" class="form-label">Subject</label>
        <select id="subject" name="subject" class="form-select">
            <option value="general">General Inquiry</option>
            <option value="support">Technical Support</option>
            <option value="feedback">Feedback</option>
        </select>
    </div>
    
    <div class="form-group">
        <label for="message" class="form-label">Message</label>
        <textarea 
            id="message" 
            name="message" 
            class="form-textarea"
            rows="5"
            placeholder="Your message here..."
            required
        ></textarea>
    </div>
    
    <div class="form-group">
        <label class="form-checkbox">
            <input type="checkbox" name="newsletter" value="yes">
            <span>Subscribe to our newsletter</span>
        </label>
    </div>
    
    <div class="form-footer">
        <button type="submit" class="submit-button">
            Send Message
        </button>
    </div>
</form>`
    }
  };

  // Get template based on type and style
  const template = templates[type]?.[style] || 'Template not found';
  
  // Customize template with user elements if provided
  return customElements ? template.replace('<!-- Custom elements -->', customElements) : template;
};

const HTMLGenerator: React.FC = () => {
  const [componentType, setComponentType] = useState<ComponentType>('layout');
  const [style, setStyle] = useState<StyleType>('modern');
  const [customElements, setCustomElements] = useState('');
  const [generatedHTML, setGeneratedHTML] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const html = generateHTML(componentType, style, customElements);
    setGeneratedHTML(html);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedHTML);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <section id="html-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-medium">
            AI-Powered HTML Generation
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          HTML Code Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Component Type
              </label>
              <select
                value={componentType}
                onChange={(e) => setComponentType(e.target.value as ComponentType)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="layout">Page Layout</option>
                <option value="form">Contact Form</option>
                <option value="card">Card Component</option>
                <option value="navigation">Navigation Menu</option>
                <option value="hero">Hero Section</option>
                <option value="footer">Footer</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as StyleType)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="minimal">Minimal</option>
                <option value="modern">Modern</option>
                <option value="classic">Classic</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Elements (Optional)
            </label>
            <textarea
              value={customElements}
              onChange={(e) => setCustomElements(e.target.value)}
              placeholder="Add custom HTML elements..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white h-32"
            />
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              className="bg-blue-600 text-white py-4 px-8 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate HTML
            </button>
          </div>

          {generatedHTML && (
            <div className="mt-12">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  Generated HTML:
                </h3>
                <button
                  onClick={copyToClipboard}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  {copied ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
              </div>
              <div className="bg-gray-900 p-6 rounded-lg overflow-x-auto">
                <pre className="text-gray-100 font-mono text-sm">
                  {generatedHTML}
                </pre>
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Customize the generated code to match your specific needs!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HTMLGenerator;